import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { adminNotificationService } from '../../../services/adminNotifications';
import type { AdminNotification } from '../../../types/adminNotification';

interface NotificationPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  notification: AdminNotification;
}

type PreviewLocale = 'en' | 'es' | 'de' | 'ru';

export const NotificationPreviewModal: React.FC<
  NotificationPreviewModalProps
> = ({ isOpen, onClose, notification }) => {
  const { t } = useTranslation('admin');
  const [activeLocale, setActiveLocale] = useState<PreviewLocale>('en');

  const PREVIEW_LOCALES: { key: PreviewLocale; label: string; flag: string }[] = [
    { key: 'en', label: t('notifications.create_modal.languages.english'), flag: '🇬🇧' },
    { key: 'es', label: t('notifications.create_modal.languages.spanish'), flag: '🇪🇸' },
    { key: 'de', label: t('notifications.create_modal.languages.german'), flag: '🇩🇪' },
    { key: 'ru', label: t('notifications.create_modal.languages.russian'), flag: '🇷🇺' },
  ];

  const { data: preview, isLoading } = useQuery({
    queryKey: ['notificationPreview', notification.id, activeLocale],
    queryFn: () =>
      adminNotificationService.preview(notification.id, activeLocale),
    enabled: isOpen,
  });

  if (!isOpen) return null;

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'announcement':
        return 'bg-blue-100 text-blue-800';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800';
      case 'security':
        return 'bg-red-100 text-red-800';
      case 'marketing':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-3xl my-8 mx-4">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold">{t('notifications.preview_modal.title')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Language Tabs */}
        <div className="border-b border-gray-200 px-6">
          <div className="flex space-x-8">
            {PREVIEW_LOCALES.map((locale) => (
              <button
                key={locale.key}
                type="button"
                onClick={() => setActiveLocale(locale.key)}
                className={`py-3 px-2 font-medium text-sm flex items-center gap-2 ${
                  activeLocale === locale.key
                    ? 'border-b-2 border-indigo-600 text-indigo-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <span className="text-lg">{locale.flag}</span>
                <span>{locale.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Preview Content */}
        <div className="px-6 py-6">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <p className="mt-2 text-gray-500">{t('notifications.preview_modal.loading')}</p>
            </div>
          ) : preview ? (
            <div className="space-y-4">
              {/* Sample User Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">
                  <strong>{t('notifications.preview_modal.sample_user')}</strong> {preview.sample_user.name} (
                  {preview.sample_user.locale})
                </p>
              </div>

              {/* Notification Card */}
              <div className="border border-gray-200 rounded-lg p-6 shadow-sm">
                {/* Type Badge */}
                <div className="flex items-center justify-between mb-4">
                  <span
                    className={`px-3 py-1 text-xs font-medium rounded-full ${getTypeColor(
                      preview.type
                    )}`}
                  >
                    {t(`notifications.types.${preview.type}`)}
                  </span>
                  {preview.expires_at && (
                    <span className="text-xs text-gray-500">
                      {t('notifications.preview_modal.expires')}:{' '}
                      {new Date(preview.expires_at).toLocaleDateString()}
                    </span>
                  )}
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {preview.title}
                </h3>

                {/* Message */}
                <div className="text-gray-700 whitespace-pre-wrap mb-4">
                  {preview.message}
                </div>

                {/* Action Button */}
                {preview.action_url && (
                  <div className="mt-4">
                    <a
                      href={preview.action_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-medium"
                    >
                      {t('notifications.preview_modal.action_button')}
                      <svg
                        className="ml-2 w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </a>
                  </div>
                )}
              </div>

              {/* Delivery Info */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">
                  {t('notifications.preview_modal.delivery_info')}
                </h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>
                    <strong>{t('notifications.preview_modal.channels')}</strong>{' '}
                    {notification.type === 'security' ||
                    notification.type === 'maintenance'
                      ? t('notifications.preview_modal.channel_email')
                      : t('notifications.preview_modal.channel_inapp')}
                  </li>
                  <li>
                    <strong>{t('notifications.preview_modal.recipients')}:</strong> {t('notifications.preview_modal.users_count', { count: notification.recipients_count })}
                  </li>
                  {notification.scheduled_at && (
                    <li>
                      <strong>{t('notifications.preview_modal.scheduled_for')}:</strong>{' '}
                      {new Date(notification.scheduled_at).toLocaleString()}
                    </li>
                  )}
                  {notification.expires_after_days && (
                    <li>
                      <strong>{t('notifications.preview_modal.expires_after')}:</strong>{' '}
                      {t('notifications.preview_modal.days', { count: notification.expires_after_days })}
                    </li>
                  )}
                </ul>
              </div>

              {/* Translation Info */}
              <div className="text-center text-sm text-gray-500 pt-4 border-t border-gray-200">
                {t('notifications.preview_modal.translation_info')}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              {t('notifications.preview_modal.error')}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
          <button onClick={onClose} className="btn btn-primary">
            {t('notifications.preview_modal.close_button')}
          </button>
        </div>
      </div>
    </div>
  );
};
